import { useState } from "react";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { Checkbox } from "@/modern/components/ui/checkbox";
import * as Editable from "@/modern/components/ui/editable";
import { cn } from "@/modern/utils";
import { MoreMenu } from "@/modern/components/more-menu";
import { Upload, Trash, Edit2Icon, ImageOff } from "lucide-react";
import { useSwivuChecklistItems } from "@/react/hooks/entityHooks/useSwivuChecklistItems";
import { Spinner } from "@/react/components/Spinner";
import { useChecklistItemUpload } from "@/react/hooks/entityHooks/useChecklistItemUpload";
import { motion } from "motion/react";

interface ChecklistItemRowComponentProps {
  className?: string;
  item: ChecklistItems;
}

export const ChecklistItemRowComponent = ({ item, className }: ChecklistItemRowComponentProps) => {
  const { toggleChecklistItem, updateChecklistItem, deleteChecklistItem, canEditChecklistItem } = useSwivuChecklistItems();
  const { fileInputRef, isUploadingImage, handleFileUpload, handleRemoveImage, triggerFileInput, allowedImageTypes } = useChecklistItemUpload(item);
  const [isEditingComment, setIsEditingComment] = useState(false);

  const canEdit = canEditChecklistItem(item);
  const src = item.image || item.thumbnailUrl;
  return (
    <div className={cn("relative flex-1 flex justify-between min-h-0 group", className, item.isItemCheck && "", item.isPhantom && " opacity-50 pointer-events-none ")}>
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedImageTypes.map((type) => `.${type}`).join(",")}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            await handleFileUpload(file);
          }
        }}
        className="hidden"
      />

      <div className="flex-1 min-w-0">
        <div className="flex gap-4">
          {/* Checkbox */}
          <div className="flex-shrink-0 ">
            <Checkbox
              className="
                transition-all duration-300 ease-out
                dark:data-[state=checked]:bg-success
                dark:data-[state=checked]:border-success
                hover:dark:border-success
                hover:border-success
                border-muted-foreground/30
                data-[state=checked]:bg-success
                data-[state=checked]:scale-110
                dark:data-[state=checked]:text-white
                size-6
                active:scale-95
              "
              checked={item.isItemCheck}
              onCheckedChange={() => toggleChecklistItem(item)}
            />
          </div>

          {/* Content Section */}
          <div className="flex-1 min-w-0">
            {/* Title/Description */}
            <Editable.Root triggerMode="dblclick" value={item.description || ""} onSubmit={(value) => updateChecklistItem(item, { description: value })} placeholder="Enter checklist item...">
              <Editable.Area>
                <Editable.Input className="px-2 font-semibold text-base w-full focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:outline outline-none ring-primary outline-primary border-none" />
                <Editable.Preview className={cn(" text-base w-full py-0")} />
              </Editable.Area>
            </Editable.Root>

            {/* Comment */}

            <Editable.Root
              triggerMode="dblclick"
              editing={isEditingComment}
              onEditingChange={(editing) => {
                if (editing === false) {
                  setIsEditingComment(false);
                }
              }}
              value={item.comment || ""}
              onSubmit={(value) => {
                updateChecklistItem(item, { comment: value });
              }}
              placeholder={isEditingComment && !item.comment ? "Add comment..." : undefined}
            >
              <Editable.Area>
                <Editable.Input className="text-sm px-2 text-muted-foreground w-full field-sizing-content outline-none min-h-[1rem]" />
                <Editable.Preview className={cn("text-sm text-muted-foreground w-full whitespace-pre-wrap py-0")} />
              </Editable.Area>
            </Editable.Root>
            {isUploadingImage && (
              <div className="flex justify-center items-center w-full aspect-video rounded-md border animate-pulse bg-muted/50">
                <Spinner />
              </div>
            )}
            {src && <img src={src} alt="Checklist item" className="w-full object-cover rounded-md border mt-1" />}
          </div>
        </div>
      </div>
      <MoreMenu
        triggerButtonClassName="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-none align-start  -mt-3"
        disabled={item.isPhantom}
        menuItems={[
          {
            itemVariant: "button" as const,
            menuText: item.comment ? "Edit Comment" : "Add Comment",
            dropdownMenuItemProps: {
              disabled: !canEdit,
              onClick: () => {
                setIsEditingComment(true);
              }
            },
            Component: () => (
              <div className="flex items-center gap-3 w-full">
                <Edit2Icon className="h-4 w-4" />
                {item.comment ? "Edit comment" : "Add comment"}
              </div>
            )
          },
          ...(!src
            ? [
                {
                  itemVariant: "button" as const,
                  menuText: "Upload Image",
                  dropdownMenuItemProps: {
                    disabled: !canEdit,
                    onClick: triggerFileInput
                  },
                  Component: () => (
                    <div className={cn("flex items-center gap-3 w-full")}>
                      <Upload className="h-4 w-4" />
                      Upload image
                    </div>
                  )
                }
              ]
            : []),

          {
            itemVariant: "separator" as const
          },
          ...(src
            ? [
                {
                  itemVariant: "button" as const,
                  menuText: "Remove Image",
                  dropdownMenuItemProps: {
                    disabled: !canEdit,
                    onClick: () => {
                      handleRemoveImage();
                    }
                  },
                  Component: () => (
                    <div className={cn("flex items-center gap-3 w-full text-destructive")}>
                      <ImageOff className="h-4 w-4" />
                      Remove image
                    </div>
                  )
                }
              ]
            : []),
          {
            itemVariant: "button" as const,
            menuText: "Delete",
            dropdownMenuItemProps: {
              disabled: !canEdit,
              className: "text-destructive focus:text-destructive",
              onClick: () => deleteChecklistItem(item)
            },
            Component: () => (
              <div className="flex items-center gap-3 w-full">
                <Trash className="h-4 w-4" />
                Delete checklist item
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

const checklistItemVariants = {
  initial: { opacity: 0, padding: 0, height: 0 },
  animate: {
    overflow: "hidden",
    height: "auto",
    opacity: 1
  },
  exit: { opacity: 0, padding: 0, height: 0, overflow: "hidden" }
};

export const AnimatedChecklistItemRow = ({ item, className }: { item: ChecklistItems; className?: string }) => {
  // return <pre>{JSON.stringify(item, null, 2)}</pre>;
  return (
    <motion.div layout variants={checklistItemVariants} className="overflow-hidden py-4" initial="inital" animate="animate" exit="exit">
      <ChecklistItemRowComponent item={item} className={className} />
    </motion.div>
  );
};
